import PageControl from '@/layouts/pdf_reporting/components/page_control/PageControl.vue';
import PageComponent from '@/layouts/pdf_reporting/components/page/PageComponent.vue';
import SectionSheet from '@/layouts/pdf_reporting/components/section_sheet/SectionSheet.vue';

export default {
  name: 'EnterpriseKYBReport',
  components: {
    PageControl,
    PageComponent,
    SectionSheet,
  },
  props: {
    kyb_data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      export_options: {
        company_information: false,
        statutory_details: false,
        principal_details: false,
        verification_summary: false,
        transaction_details: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some(option => option);
    },
    
    safe_kyb_data() {
      return this.kyb_data || {};
    },

    sheet_elements() {
      return ['PageComponent'];
    },

    // Company Information - Updated for TransUnion API structure
    company_name() {
      return this.safe_kyb_data?.FirstResponse?.BusinessName || 
             this.safe_kyb_data?.Header?.BusinessName ||
             this.safe_kyb_data?.Names?.BusinessName ||
             this.safe_kyb_data?.CompanySearch?.BusinessName ||
             'Not Available';
    },

    registration_number() {
      return this.safe_kyb_data?.FirstResponse?.RegistrationNumber || 
             this.safe_kyb_data?.RegistrationDetails?.RegistrationNo ||
             this.safe_kyb_data?.CompanySearch?.RegNo ||
             'Not Available';
    },

    company_status() {
      return this.safe_kyb_data?.RegistrationDetails?.RegistrationStatusDesc || 
             this.safe_kyb_data?.CompanySearch?.RegStatus ||
             'Not Available';
    },

    registration_date() {
      const regDate = this.safe_kyb_data?.RegistrationDetails?.RegistrationDate || 
                     this.safe_kyb_data?.Header?.StartDate;
      if (regDate) {
        // TransUnion dates are in YYYYMMDD format
        const year = regDate.substring(0, 4);
        const month = regDate.substring(4, 6);
        const day = regDate.substring(6, 8);
        return this.formatDate(`${year}-${month}-${day}`);
      }
      return 'Not Available';
    },

    business_type() {
      return this.safe_kyb_data?.Header?.BusinessType || 
             this.safe_kyb_data?.RegistrationDetails?.CompanyTypeDesc || 
             'Not Available';
    },

    tax_number() {
      return this.safe_kyb_data?.Header?.TaxNumber || 
             'Not Available';
    },

    vat_number() {
      const vatNumbers = this.safe_kyb_data?.Header?.VATNumbers?.string;
      if (vatNumbers && Array.isArray(vatNumbers) && vatNumbers.length > 0) {
        // Return the first non-zero VAT number
        const validVat = vatNumbers.find(vat => vat && vat !== '000000000000000');
        return validVat || 'Not Available';
      }
      return 'Not Available';
    },

    registered_address() {
      const header = this.safe_kyb_data?.Header;
      const regDetails = this.safe_kyb_data?.RegistrationDetails;
      
      if (header?.PhysicalAddress) {
        const addressParts = [
          header.PhysicalAddress,
          header.Suburb,
          header.City,
          header.Country,
          header.PostCode
        ].filter(part => part && part.trim());
        
        return addressParts.join(', ') || 'Not Available';
      } else if (regDetails?.RegisteredAddress) {
        const addressParts = [
          regDetails.RegisteredAddress,
          regDetails.Suburb,
          regDetails.City,
          regDetails.Country,
          regDetails.PostCode
        ].filter(part => part && part.trim());
        
        return addressParts.join(', ') || 'Not Available';
      }
      
      return 'Not Available';
    },

    industry_classification() {
      return this.safe_kyb_data?.Header?.Industry || 
             this.safe_kyb_data?.Header?.BusinessFunction ||
             'Not Available';
    },

    // Verification Results - Updated for TransUnion API structure
    company_registration_found() {
      return this.registration_number !== 'Not Available' ? 'Yes' : 'No';
    },

    registration_number_match() {
      return this.registration_number !== 'Not Available' ? 'Yes' : 'No';
    },

    company_name_match() {
      return this.company_name !== 'Not Available' ? 'Yes' : 'No';
    },

    company_active_status() {
      return this.company_status === 'In Business' || this.company_status === 'Active' ? 'Yes' : 'No';
    },

    tax_clearance_verified() {
      return this.tax_number !== 'Not Available' ? 'Yes' : 'No';
    },

    bank_account_verified() {
      // This would depend on additional verification data
      return 'Not Available';
    },

    address_verified() {
      return this.registered_address !== 'Not Available' ? 'Yes' : 'No';
    },

    principal_information_available() {
      const principals = this.principal_details;
      return principals && principals.length > 0 ? 'Yes' : 'No';
    },

    // Overall Status
    overall_verification_status() {
      const criticalChecks = [
        this.company_registration_found,
        this.registration_number_match,
        this.company_name_match,
        this.company_active_status
      ];
      
      const passedChecks = criticalChecks.filter(check => check === 'Yes').length;
      const totalChecks = criticalChecks.length;
      
      if (passedChecks === totalChecks) {
        return 'Verified';
      } else if (passedChecks > totalChecks / 2) {
        return 'Partially Verified';
      }
      return 'Not Verified';
    },

    compliance_status() {
      // Based on principal information and company status
      if (this.company_active_status === 'Yes' && this.principal_information_available === 'Yes') {
        return 'Compliant';
      }
      return 'Review Required';
    },

    risk_level() {
      // Simple risk assessment based on available data
      if (this.overall_verification_status === 'Verified') {
        return 'Low';
      } else if (this.overall_verification_status === 'Partially Verified') {
        return 'Medium';
      }
      return 'High';
    },

    risk_score() {
      // Calculate risk score based on verification status
      const criticalChecks = [
        this.company_registration_found,
        this.registration_number_match,
        this.company_name_match,
        this.company_active_status
      ];
      
      const passedChecks = criticalChecks.filter(check => check === 'Yes').length;
      return Math.round((passedChecks / criticalChecks.length) * 100);
    },

    // Statutory Details - Updated for TransUnion API structure
    statutory_details() {
      const principals = this.safe_kyb_data?.Principals?.Principal;
      if (!principals || !Array.isArray(principals)) return [];

      return principals.map(principal => {
        const fullName = [
          principal.Forename1,
          principal.Forename2,
          principal.Forename3,
          principal.Surname
        ].filter(name => name && name.trim()).join(' ');

        return {
          type: 'Director',
          name: fullName || 'Not Available',
          id_number: principal.IDNumber || 'Not Available',
          position: principal.Position || 'Not Available',
          date_started: principal.DateStarted ? this.formatTransUnionDate(principal.DateStarted) : 'Not Available',
          status: principal.ConfirmedByRegistrar === 'Y' ? 'Active' : 'Unconfirmed',
          verification_status: principal.ConfirmedByRegistrar === 'Y' ? 'Verified' : 'Unverified'
        };
      });
    },

    // Principal Details - Updated for TransUnion API structure
    principal_details() {
      const principals = this.safe_kyb_data?.Principals?.Principal;
      if (!principals || !Array.isArray(principals)) return [];

      return principals.slice(0, 5).map(principal => { // Limit to first 5 for display
        const fullName = [
          principal.Forename1,
          principal.Forename2,
          principal.Forename3,
          principal.Surname
        ].filter(name => name && name.trim()).join(' ');

        return {
          name: fullName || 'Not Available',
          position: principal.Position || 'Not Available',
          id_number: principal.IDNumber || 'Not Available',
          email: 'Not Available', // Not provided in TransUnion data
          phone: 'Not Available', // Not provided in TransUnion data
          id_verified: principal.IDNumber ? 'Yes' : 'No',
          address_verified: 'Not Available', // Would need additional data
          documents_verified: principal.ConfirmedByRegistrar === 'Y' ? 'Yes' : 'No',
          biometric_verified: 'Not Available', // Not applicable for TransUnion data
          overall_status: principal.ConfirmedByRegistrar === 'Y' ? 'Verified' : 'Unverified'
        };
      });
    },

    // Transaction Details - Updated for TransUnion API structure
    transaction_details() {
      return {
        service_execution_time: 'Not Available',
        time_of_request: this.safe_kyb_data?.Header?.DateOfHeader ? 
          this.formatTransUnionDate(this.safe_kyb_data.Header.DateOfHeader) : 'Not Available',
        verification_decision: this.overall_verification_status,
        recommended_action: this.overall_verification_status === 'Verified' ? 
          'Proceed with onboarding' : 'Additional verification required',
        compliance_score: this.risk_score,
        application_id: this.safe_kyb_data?.FirstResponse?.ITNumber || 'Not Available',
        solution_instance_id: this.safe_kyb_data?.FirstResponse?.TicketNumber || 'Not Available'
      };
    },

    // Report metadata
    report_reference() {
      return this.safe_kyb_data?.FirstResponse?.ITNumber || 
             this.safe_kyb_data?.FirstResponse?.TicketNumber || 
             `KYB-${Date.now()}`;
    },

    report_date() {
      const reportDate = this.safe_kyb_data?.Header?.DateOfHeader;
      if (reportDate) {
        return this.formatTransUnionDate(reportDate);
      }
      return this.formatDate(new Date());
    },

    // Additional computed properties for template
    formatted_current_date() {
      return this.formatDate(new Date());
    },

    formatted_verification_date() {
      const verificationDate = this.safe_kyb_data?.Fields?.CompanyDetails?.TransactionDetails?.TimeOfRequest || new Date().toISOString();
      return this.formatDate(verificationDate);
    },

    verification_status_badge_class() {
      return this.overall_verification_status === 'Verified' ? 'text-success' : 'text-danger';
    },

    // Status badge styling
    verification_status_badge() {
      return this.overall_verification_status === 'Verified' ? 'badge-success' : 'badge-danger';
    },

    verification_status_icon() {
      return this.overall_verification_status === 'Verified' ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },

    modal_instance() {
      return new window.bootstrap.Modal(document.getElementById(`export-modal-${this.report_reference}`));
    }
  },

  methods: {
    
    close_modal() {
      const modal = document.getElementById(`export-modal-${this.report_reference}`);
      if (modal) {
        const modalInstance = window.bootstrap.Modal.getInstance(modal);
        if (modalInstance) {
          modalInstance.hide();
        }
      }
    },
    
    // Utility methods
    formatDate(date) {
      if (!date) return 'Not Available';
      
      try {
        // Handle different date formats
        if (typeof date === 'string' && date.length === 8) {
          // YYYYMMDD format from TransUnion
          return this.formatTransUnionDate(date);
        }
        
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'Invalid Date';
        return d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (error) {
        console.error('Date formatting error:', error);
        return 'Not Available';
      }
    },
    
    get_verification_badge_class(result) {
      if (result === 'Yes' || result === 'Pass' || result === 'Verified') {
        return 'badge-success';
      } else if (result === 'No' || result === 'Fail' || result === 'Failed') {
        return 'badge-danger';
      } else {
        return 'badge-warning';
      }
    },
    
    get_verification_icon(result) {
      if (result === 'Yes' || result === 'Pass' || result === 'Verified') {
        return 'fas fa-check';
      } else if (result === 'No' || result === 'Fail' || result === 'Failed') {
        return 'fas fa-times';
      } else {
        return 'fas fa-question';
      }
    },
    
    get_verification_status_text(result) {
      if (result === 'Yes' || result === 'Pass' || result === 'Verified') {
        return 'Pass';
      } else if (result === 'No' || result === 'Fail' || result === 'Failed') {
        return 'Fail';
      } else {
        return 'N/A';
      }
    },

    get_status_badge_class(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'badge-success';
        case 'inactive':
        case 'resigned':
          return 'badge-danger';
        case 'pending':
          return 'badge-warning';
        default:
          return 'badge-secondary';
      }
    },
    
    // Export functionality
    show_export_modal() {
      this.export_options.company_information = true;
      this.export_options.statutory_details = true;
      this.export_options.principal_details = true;
      this.export_options.verification_summary = true;
      this.export_options.transaction_details = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },
    
    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js');
        this.libraries_loaded.papa_parse = true;
      }
      
      if (!this.libraries_loaded.sheetjs) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js');
        this.libraries_loaded.sheetjs = true;
      }
    },
    
    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    async export_data() {
      await this.load_export_libraries();
      
      if (this.export_format === 'csv') {
        await this.export_csv();
      } else if (this.export_format === 'excel') {
        await this.export_excel();
      } else if (this.export_format === 'json') {
        await this.export_json();
      }
      
      // Close modal
      this.close_modal();
    },

    // Prepare company information data
    prepare_company_information_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Company Name": this.company_name,
          "Registration Number": this.registration_number,
          "Company Status": this.company_status,
          "Registration Date": this.registration_date,
          "Business Type": this.business_type,
          "Industry Classification": this.industry_classification,
          "Verification Status": this.overall_verification_status,
          "Verification Date": this.formatted_verification_date,
          "Generated Date": this.formatted_current_date,
        }
      ];
    },

    // Prepare statutory details data
    prepare_statutory_details_data() {
      return this.statutory_details.map(detail => ({
        "Field": detail.field,
        "Value": detail.value,
        "Description": detail.description
      }));
    },

    // Prepare principal details data
    prepare_principal_details_data() {
      return this.principal_details.map(principal => ({
        "Name": principal.name,
        "ID Number": principal.id_number,
        "Role": principal.role,
        "Appointment Date": principal.appointment_date,
        "Status": principal.status
      }));
    },

    // Prepare verification summary data
    prepare_verification_summary_data() {
      return [
        {
          "Verification Check": "Company Registration",
          "Description": "Verify if company is registered with CIPC",
          "Result": this.company_registration_found,
          "Status": this.get_verification_status_text(this.company_registration_found)
        },
        {
          "Verification Check": "Registration Number Match",
          "Description": "Verify if registration number matches records",
          "Result": this.registration_number_match,
          "Status": this.get_verification_status_text(this.registration_number_match)
        },
        {
          "Verification Check": "Company Name Match",
          "Description": "Verify if company name matches records",
          "Result": this.company_name_match,
          "Status": this.get_verification_status_text(this.company_name_match)
        },
        {
          "Verification Check": "Company Active Status",
          "Description": "Verify if company is currently active",
          "Result": this.company_active_status,
          "Status": this.get_verification_status_text(this.company_active_status)
        },
        {
          "Verification Check": "Principal Information Available",
          "Description": "Verify if principal information is available",
          "Result": this.principal_information_available,
          "Status": this.get_verification_status_text(this.principal_information_available)
        }
      ];
    },

    // Prepare transaction details data
    prepare_transaction_details_data() {
      return [
        {
          "Transaction Reference": this.report_reference,
          "Enquiry Reference": this.enquiry_reference,
          "Enquiry Reason": this.enquiry_reason,
          "Service Name": this.service_name,
          "Subscriber Name": this.subscriber_name,
          "Internal Reference": this.internal_reference,
          "Product List": this.product_list,
          "Company Name Requested": this.company_name,
          "Registration Number Requested": this.registration_number,
          "Verification Date": this.formatted_verification_date,
          "Generated Date": this.formatted_current_date
        }
      ];
    },

    // Prepare raw JSON data
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          enquiry_reference: this.enquiry_reference,
          generated_date: this.formatted_current_date,
          export_date: new Date().toISOString(),
          report_type: 'Enterprise KYB Verification Report'
        },
        raw_kyb_data: this.safe_kyb_data
      };
    },

    // Export methods
    async export_csv() {
      const Papa = window.Papa;
      const current_date = new Date().toISOString().split('T')[0];
      
      // Map section keys to their data preparation methods and file suffixes
      const sectionMap = [
        {
          key: 'company_information',
          label: 'Company_Information',
          prepare: this.prepare_company_information_data
        },
        {
          key: 'statutory_details',
          label: 'Statutory_Details',
          prepare: this.prepare_statutory_details_data
        },
        {
          key: 'principal_details',
          label: 'Principal_Details',
          prepare: this.prepare_principal_details_data
        },
        {
          key: 'verification_summary',
          label: 'Verification_Summary',
          prepare: this.prepare_verification_summary_data
        },
        {
          key: 'transaction_details',
          label: 'Transaction_Details',
          prepare: this.prepare_transaction_details_data
        }
      ];

      // For each selected section, generate and download a CSV
      for (const section of sectionMap) {
        if (this.export_options[section.key]) {
          const data = section.prepare.call(this);
          if (data && data.length > 0) {
            const csv = Papa.unparse(data);
            const filename = `Enterprise_KYB_Report_${section.label}_${current_date}.csv`;
            this.download_file(csv, filename, 'text/csv');
          }
        }
      }
    },

    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = new Date().toISOString().split('T')[0];
      const workbook = XLSX.utils.book_new();
      
      if (this.export_options.company_information) {
        const company_data = this.prepare_company_information_data();
        const worksheet = XLSX.utils.json_to_sheet(company_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Company Information');
      }
      
      if (this.export_options.statutory_details) {
        const statutory_data = this.prepare_statutory_details_data();
        const worksheet = XLSX.utils.json_to_sheet(statutory_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Statutory Details');
      }
      
      if (this.export_options.principal_details) {
        const principal_data = this.prepare_principal_details_data();
        const worksheet = XLSX.utils.json_to_sheet(principal_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Principal Details');
      }
      
      if (this.export_options.verification_summary) {
        const verification_data = this.prepare_verification_summary_data();
        const worksheet = XLSX.utils.json_to_sheet(verification_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Verification Summary');
      }
      
      if (this.export_options.transaction_details) {
        const transaction_data = this.prepare_transaction_details_data();
        const worksheet = XLSX.utils.json_to_sheet(transaction_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transaction Details');
      }
      
      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, create a simple table with the JSON as text
        const json_table = [
          {
            'JSON Data': JSON.stringify(json_data, null, 2)
          }
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Raw JSON Data');
      }
      
      const filename = `Enterprise_KYB_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },

    async export_json() {
      const current_date = new Date().toISOString().split('T')[0];
      const export_data = {};
      
      if (this.export_options.company_information) {
        export_data.company_information = this.prepare_company_information_data()[0];
      }
      
      if (this.export_options.statutory_details) {
        export_data.statutory_details = this.prepare_statutory_details_data();
      }
      
      if (this.export_options.principal_details) {
        export_data.principal_details = this.prepare_principal_details_data();
      }
      
      if (this.export_options.verification_summary) {
        export_data.verification_summary = this.prepare_verification_summary_data();
      }
      
      if (this.export_options.transaction_details) {
        export_data.transaction_details = this.prepare_transaction_details_data()[0];
      }
      
      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }
      
      const json = JSON.stringify(export_data, null, 2);
      const filename = `Enterprise_KYB_Report_${current_date}.json`;
      this.download_file(json, filename, 'application/json');
    },

    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    // Validation method to test export data completeness
    validate_export_data() {
      const validation_report = {
        company_information: {
          available: !!this.company_name && !!this.registration_number,
          data_count: this.prepare_company_information_data().length
        },
        statutory_details: {
          available: this.statutory_details.length > 0,
          data_count: this.prepare_statutory_details_data().length
        },
        principal_details: {
          available: this.principal_details.length > 0,
          data_count: this.prepare_principal_details_data().length
        },
        verification_summary: {
          available: true,
          data_count: this.prepare_verification_summary_data().length
        },
        transaction_details: {
          available: !!this.enquiry_reference,
          data_count: this.prepare_transaction_details_data().length
        }
      };
      
      return validation_report;
    },

    formatTransUnionDate(dateString) {
      if (!dateString || dateString.length !== 8) return 'Not Available';
      
      try {
        // TransUnion date format: YYYYMMDD
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return 'Not Available';
        
        return date.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      } catch (error) {
        console.error('TransUnion date formatting error:', error);
        return 'Not Available';
      }
    },

    exportToCSV() {
      if (typeof window.Papa === 'undefined') {
        console.error('PapaParse library not loaded');
        return;
      }
      
      const csvData = this.prepare_export_data();
      const csv = window.Papa.unparse(csvData);
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `enterprise_kyb_report_${this.report_reference}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    exportToExcel() {
      if (typeof window.XLSX === 'undefined') {
        console.error('SheetJS (XLSX) library not loaded');
        return;
      }
      
      const data = this.prepare_export_data();
      const wb = window.XLSX.utils.book_new();
      const ws = window.XLSX.utils.json_to_sheet(data);
      
      window.XLSX.utils.book_append_sheet(wb, ws, 'Enterprise KYB Report');
      window.XLSX.writeFile(wb, `enterprise_kyb_report_${this.report_reference}.xlsx`);
    },

    exportToJSON() {
      const data = {
        report_metadata: {
          report_reference: this.report_reference,
          report_date: this.report_date,
          export_timestamp: new Date().toISOString()
        },
        company_information: {
          company_name: this.company_name,
          registration_number: this.registration_number,
          company_status: this.company_status,
          registration_date: this.registration_date,
          business_type: this.business_type,
          tax_number: this.tax_number,
          vat_number: this.vat_number,
          registered_address: this.registered_address,
          industry_classification: this.industry_classification
        },
        verification_results: {
          company_registration_found: this.company_registration_found,
          registration_number_match: this.registration_number_match,
          company_name_match: this.company_name_match,
          company_active_status: this.company_active_status,
          tax_clearance_verified: this.tax_clearance_verified,
          bank_account_verified: this.bank_account_verified,
          address_verified: this.address_verified,
          principal_information_available: this.principal_information_available,
          overall_verification_status: this.overall_verification_status,
          compliance_status: this.compliance_status,
          risk_level: this.risk_level,
          risk_score: this.risk_score
        },
        statutory_details: this.statutory_details,
        principal_details: this.principal_details,
        transaction_details: this.transaction_details,
      };
      
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `enterprise_kyb_report_${this.report_reference}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    prepare_export_data() {
      const exportData = [];
      
      // Company Information
      exportData.push({
        Category: 'Company Information',
        Field: 'Company Name',
        Value: this.company_name
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'Registration Number',
        Value: this.registration_number
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'Company Status',
        Value: this.company_status
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'Registration Date',
        Value: this.registration_date
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'Business Type',
        Value: this.business_type
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'Tax Number',
        Value: this.tax_number
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'VAT Number',
        Value: this.vat_number
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'Registered Address',
        Value: this.registered_address
      });
      exportData.push({
        Category: 'Company Information',
        Field: 'Industry Classification',
        Value: this.industry_classification
      });

      // Verification Results
      exportData.push({
        Category: 'Verification Results',
        Field: 'Company Registration Found',
        Value: this.company_registration_found
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Registration Number Match',
        Value: this.registration_number_match
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Company Name Match',
        Value: this.company_name_match
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Company Active Status',
        Value: this.company_active_status
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Tax Clearance Verified',
        Value: this.tax_clearance_verified
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Bank Account Verified',
        Value: this.bank_account_verified
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Address Verified',
        Value: this.address_verified
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Principal Information Available',
        Value: this.principal_information_available
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Overall Verification Status',
        Value: this.overall_verification_status
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Compliance Status',
        Value: this.compliance_status
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Risk Level',
        Value: this.risk_level
      });
      exportData.push({
        Category: 'Verification Results',
        Field: 'Risk Score',
        Value: this.risk_score
      });

      // Statutory Details
      this.statutory_details.forEach((detail, index) => {
        exportData.push({
          Category: 'Statutory Details',
          Field: `${detail.type} ${index + 1} - Name`,
          Value: detail.name
        });
        exportData.push({
          Category: 'Statutory Details',
          Field: `${detail.type} ${index + 1} - ID Number`,
          Value: detail.id_number
        });
        exportData.push({
          Category: 'Statutory Details',
          Field: `${detail.type} ${index + 1} - Position`,
          Value: detail.position
        });
        exportData.push({
          Category: 'Statutory Details',
          Field: `${detail.type} ${index + 1} - Date Started`,
          Value: detail.date_started
        });
        exportData.push({
          Category: 'Statutory Details',
          Field: `${detail.type} ${index + 1} - Status`,
          Value: detail.status
        });
        exportData.push({
          Category: 'Statutory Details',
          Field: `${detail.type} ${index + 1} - Verification Status`,
          Value: detail.verification_status
        });
      });

      // Principal Details
      this.principal_details.forEach((principal, index) => {
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Name`,
          Value: principal.name
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Position`,
          Value: principal.position
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - ID Number`,
          Value: principal.id_number
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Email`,
          Value: principal.email
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Phone`,
          Value: principal.phone
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - ID Verified`,
          Value: principal.id_verified
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Address Verified`,
          Value: principal.address_verified
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Documents Verified`,
          Value: principal.documents_verified
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Biometric Verified`,
          Value: principal.biometric_verified
        });
        exportData.push({
          Category: 'Principal Details',
          Field: `Principal ${index + 1} - Overall Status`,
          Value: principal.overall_status
        });
      });

      return exportData;
    },

    openExportModal() {
      const modalElement = document.getElementById('exportModal');
      if (modalElement && window.bootstrap) {
        const modal = new window.bootstrap.Modal(modalElement);
        modal.show();
      } else {
        console.error('Bootstrap modal not available');
      }
    },
  }
};
